import { Box, Stack, Theme, useMediaQuery } from '@mui/material';
import React, { useState } from 'react';
import { CampaignTypeSelectionDialog } from 'src/modules/campaigns/components';
import { CreateKeywordFormDialog } from 'src/modules/keywords/components';
import { KeywordFormData } from 'src/modules/keywords/types';
import AwayMessageSwitcher from 'src/modules/campaigns/components/AwayMessage/AwayMessageSwitcher';
import { useHomeViewController } from '../../controllers';
import AnalyticsSection from '../AnalyticsSection';
import HomeActionButtons from '../HomeActionButtons';
import HomeGreeting from '../HomeGreeting';
import StatLegend from '../StatLegend';
import SubscriptionCard from '../SubscriptionCard';
import ValidationBanner from '../ValidationBanner';

export default function HomeView() {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const c = useHomeViewController();

  const [customAnchorEl, setCustomAnchorEl] = useState<null | HTMLElement>(null);
  const customOpen = Boolean(customAnchorEl);

  const handleCustomOpen = (event: React.MouseEvent<HTMLElement>) => {
    setCustomAnchorEl(event.currentTarget);
  };
  const handleCustomClose = () => setCustomAnchorEl(null);

  const onCreateCampaign = () => c.campaignTypeSelectionDialogController.open();
  const onAddKeyword = () => c.createKeywordFormDialogController.open();
  const onManageContacts = () => c.navigate('/groups');

  return (
    <>
      <Stack direction="row" alignItems="center" mb={isMobile ? 1 : 0}>
        <Box width="76%">
          <HomeGreeting firstName={c.profile?.firstName} isMobile={isMobile} />
        </Box>
        <Box width="24%">
          <AwayMessageSwitcher />
        </Box>
      </Stack>

      {isMobile && (
        <HomeActionButtons
          isMobile
          onCreateCampaign={onCreateCampaign}
          onAddKeyword={onAddKeyword}
          onManageContacts={onManageContacts}
        />
      )}

      <Stack direction={isMobile ? 'column' : 'row'} mb={1}>
        <SubscriptionCard
          company={c.company ?? undefined}
          companyLoading={c.companyLoading}
          isMobile={isMobile}
          creditsInPercents={c.creditsInPercents}
          selectedCompanyId={c.selectedCompanyId}
          profile={c.profile}
          onCompanyRefetch={() => c.refetchCompany()}
        />
        {!isMobile && (
          <HomeActionButtons
            isMobile={false}
            onCreateCampaign={onCreateCampaign}
            onAddKeyword={onAddKeyword}
            onManageContacts={onManageContacts}
          />
        )}
      </Stack>

      <ValidationBanner
        company={c.company ?? undefined}
        validationInProgress={c.validationInProgress}
        onValidate={c.handleValidateContacts}
      />

      <Box mt={1.25} pb={2.5}>
        <StatLegend
          items={[
            { label: 'Total Contacts', value: c.counters.contactsCount || 0 },
            { label: 'Active Groups', value: c.counters.activeGroupsCount || 0 },
            { label: 'Active Campaigns', value: c.counters.activeCampaignsCount || 0 },
            { label: 'Total Keywords', value: c.counters.keywordCount || 0 },
          ]}
          loading={c.countersLoading}
          sx={{ mb: 2 }}
        />
        <AnalyticsSection
          timePeriod={c.timePeriod}
          onPresetChange={c.setTimePeriod}
          customOpen={customOpen}
          customAnchorEl={customAnchorEl}
          onCustomOpen={handleCustomOpen}
          onCustomClose={handleCustomClose}
          startDateInterval={c.startDateInterval}
          endDateInterval={c.endDateInterval}
          onCustomRangeChange={c.setCustomRange}
          granularity={c.granularity}
          onGranularityChange={c.setGranularity}
          metricsLoading={c.metricsLoading}
          chartData={c.chartData}
          creditChartConfig={c.creditChartConfig}
          messageTypeConfig={c.messageTypeConfig}
          campaignInboxConfig={c.campaignInboxConfig}
          onCreditTypeChange={c.setCreditChartType}
          onMessageTypeChange={c.setMessageTypeType}
          onCampaignInboxTypeChange={c.setCampaignInboxType}
          customLabel={c.customLabel}
        />
      </Box>

      <CreateKeywordFormDialog
        {...c.createKeywordFormDialogController}
        submit={(full: KeywordFormData, dirty: Partial<KeywordFormData>) =>
          c.createKeywordFormDialogController.submit(full, dirty, c.refreshKeywords)
        }
      />

      <CampaignTypeSelectionDialog {...c.campaignTypeSelectionDialogController} />
    </>
  );
}
